import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { ActivitySourceSchema, ActivitySubjectTypeSchema } from '../activity/activities.schemas';
import {
  ActivityRecordCommunicationTypeSchema,
  ActivityRecordDirectionSchema,
  ActivityRecordKindSchema,
  ActivityProjectionLinkSchema,
  ActivityRecordStateSchema,
  ActivityOutcomeSchema,
  AppointmentTypeSchema,
} from '../activity/activity-records.schemas';

// Emitted when a plannable activity is written to the shared store (IMM-430.1).
// The activity module subscribes to its own event and projects it onto the
// unified timeline — the store and the projection stay separate concerns.
//
// `state`, `dueAt` and `assigneeMemberId` travel on the payload because the
// projected body must carry them: the frontend partitions Anstehend/Verlauf on
// `body.state` (the shipped IMM-418 contract).
export const ActivityRecordedPayloadSchema = z.object({
  activityId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid().nullable(),
  actor: ActorSchema,
  subjectType: ActivitySubjectTypeSchema,
  subjectId: z.uuid(),
  kind: ActivityRecordKindSchema,
  state: ActivityRecordStateSchema,
  content: z.string(),
  occurredAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  assigneeMemberId: z.uuid().nullable(),
  type: ActivityRecordCommunicationTypeSchema.nullable(),
  direction: ActivityRecordDirectionSchema.nullable(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  appointmentType: AppointmentTypeSchema.nullable(),
  outcome: ActivityOutcomeSchema.nullable().default(null),
  // Which surface performed the write (IMM-601.4): APP, or CONNECTED_CLIENT for
  // a workspace-MCP tool call. Travels on the payload so the projected timeline
  // body can render the „Über einen verbundenen Client" marker. Optional with a
  // default so an event already sitting in the outbox across the deploy still
  // parses — absent means APP.
  source: ActivitySourceSchema.default('APP'),
  // The subject itself plus every caller-supplied link, deduped. This is what
  // keeps a deal activity visible on its contact's timeline.
  links: z.array(ActivityProjectionLinkSchema),
});

export type ActivityRecordedPayload = z.infer<typeof ActivityRecordedPayloadSchema>;

// ── Transition events (IMM-430.3) ─────────────────────────────────────────────
//
// Each carries what the projection needs to AMEND the existing timeline entry —
// the frontend partitions Anstehend/Verlauf on `body.state`, so an unamended
// body leaves a completed item stuck under Anstehend forever.
//
// The kind attributes travel here too, and they are NOT redundant with the
// recorded event: an amend REPLACES the projected body rather than merging into
// it (`activity_contents` stores one row per version and the read takes the
// highest). A transition that omitted them would drop `communicationType` and
// `direction` from the body, `CommunicationBody` would fail to parse, and the
// timeline would render nothing at all for a completed communication. This
// mirrors the lead transition parsers, which re-emit the whole body every time.
const ActivityTransitionBaseSchema = z.object({
  activityId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid().nullable(),
  actor: ActorSchema,
  subjectType: ActivitySubjectTypeSchema,
  subjectId: z.uuid(),
  kind: ActivityRecordKindSchema,
  state: ActivityRecordStateSchema,
  content: z.string(),
  assigneeMemberId: z.uuid().nullable(),
  type: ActivityRecordCommunicationTypeSchema.nullable(),
  direction: ActivityRecordDirectionSchema.nullable(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  appointmentType: AppointmentTypeSchema.nullable(),
  // Which surface performed THIS transition (IMM-601.4) — not the row's stored
  // creation source. The projected body is replaced wholesale per transition, so
  // the timeline marker reflects the latest action's surface. Defaulted for
  // events already in flight across the deploy; absent means APP.
  source: ActivitySourceSchema.default('APP'),
});

export const ActivityCompletedPayloadSchema = ActivityTransitionBaseSchema.extend({
  occurredAt: z.iso.datetime(),
  outcome: ActivityOutcomeSchema.nullable(),
});
export type ActivityCompletedPayload = z.infer<typeof ActivityCompletedPayloadSchema>;

export const ActivityRescheduledPayloadSchema = ActivityTransitionBaseSchema.extend({
  dueAt: z.iso.datetime(),
});
export type ActivityRescheduledPayload = z.infer<typeof ActivityRescheduledPayloadSchema>;

export const ActivityUpdatedPayloadSchema = ActivityTransitionBaseSchema.extend({
  occurredAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  outcome: ActivityOutcomeSchema.nullable(),
  contentEdited: z.boolean(),
});
export type ActivityUpdatedPayload = z.infer<typeof ActivityUpdatedPayloadSchema>;

export const ActivityCancelledPayloadSchema = ActivityTransitionBaseSchema.extend({
  reason: z.string().nullable(),
  // When it was cancelled. The row keeps `occurred_at` null — a cancelled
  // activity never happened — but the timeline still has to sort the entry
  // somewhere, and the cancellation instant is the only honest answer.
  cancelledAt: z.iso.datetime(),
});
export type ActivityCancelledPayload = z.infer<typeof ActivityCancelledPayloadSchema>;

export const ActivityReassignedPayloadSchema = ActivityTransitionBaseSchema.extend({
  dueAt: z.iso.datetime().nullable(),
  previousAssigneeMemberId: z.uuid().nullable(),
});
export type ActivityReassignedPayload = z.infer<typeof ActivityReassignedPayloadSchema>;

// An e-mail activity was recorded, moved, re-stamped or deleted by the thread
// reconcile (IMM-866.1). The projection handler re-reads the row and re-projects
// it (or retracts it when gone).
export const EmailActivityChangedPayloadSchema = z.object({
  workspaceId: z.uuid(),
  activityId: z.uuid(),
});

export type EmailActivityChangedPayload = z.infer<typeof EmailActivityChangedPayloadSchema>;

// Some Kontakt's or Lead's counted e-mail set changed (IMM-866.3). Carries no value on purpose:
// the subscriber recomputes from the store, so a late or reordered delivery writes the current truth.
export const EmailLastContactChangedPayloadSchema = z.object({
  workspaceId: z.uuid(),
  subjectType: z.enum(['CONTACT', 'LEAD']),
  subjectId: z.uuid(),
});

export type EmailLastContactChangedPayload = z.infer<typeof EmailLastContactChangedPayloadSchema>;

/**
 * A member deleted an e-mail activity (IMM-1095). The write's audit record;
 * `email-activity-changed` is the value-less projection trigger beside it.
 */
export const EmailActivityDeletedPayloadSchema = z.object({
  activityId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type EmailActivityDeletedPayload = z.infer<typeof EmailActivityDeletedPayloadSchema>;
